import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { useAppDispatch } from "@/store";
import fast from "@/assets/images/fast.svg";
import {
  fetchCategoryFiltersById,
  fetchCategoryFiltersByIdWizzy,
  fetchCategoryMappingById,
  fetchSearchFilters,
  fetchSearchProductBywizzy,
  selectCategory,
} from "@/store/slices/category";
import styles from "@/styles/product_list.module.scss";
import { combineCategoryFilters } from "@/utils/combineCategoryFilters";
import { resolveCategorySlug } from "@/utils/resolveCategorySlug";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PaginationCounter from "../pagination-counter/pagination-counter";
import CollectionFilter from "./collection-filter/collection-filter";
import MetalFilter from "./metal-filter/metal-filter";
import PriceFilter from "./price-filter/price-filter";
import SortFilter from "./sort-filter/sort-filter";
import OccasionFilter from "./occasion-filter/occasion-filter";
import DesktopFilterDropdown from "./desktop-filter-dropdown/desktop-filter-dropdown";

const CatgeoryFiltersUnbxd = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const categoryState = useSelector(selectCategory);
  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [isSearch, setIsSearch] = useState(false);

  const priceFilters = [
    { label: "Below ₹ 10,000", value: { max: 10000 } },
    { label: "₹ 10,000 - ₹ 20,000", value: { min: 10000, max: 20000 } },
    { label: "₹ 20,000 - ₹ 30,000", value: { min: 20000, max: 30000 } },
    { label: "₹ 30,000 - ₹ 40,000", value: { min: 30000, max: 40000 } },
    { label: "₹ 40,000 - ₹ 50,000", value: { min: 40000, max: 50000 } },
    { label: "₹ 50,000 and Above", value: { min: 50000 } },
  ];

  useEffect(() => {
    if (router.route == "/jewellery") {
      setIsSearch(true);
    }
  }, [router.route]);
  useEffect(() => {
    if (router.route === "/jewellery" && router.query?.q) {
      setShowFilter(false);
    }
  }, [router.route, router.query?.q]);

  useEffect(() => {
    if (!isSearch && categoryState.data) {
      dispatch(fetchCategoryMappingById(categoryState.data.id));
    }
  }, [categoryState.data, dispatch, isSearch]);

  const activeCategory = !isSearch ? categoryState?.data?.name : null;

  let activePriceLabel: string | null = null;
  const qPriceMin = router.query.priceMin
    ? Number(router.query.priceMin)
    : undefined;
  const qPriceMax = router.query.priceMax
    ? Number(router.query.priceMax)
    : undefined;

  const activePriceObj = priceFilters.find(
    (p) => p.value.min === qPriceMin && p.value.max === qPriceMax
  );
  if (activePriceObj) {
    activePriceLabel = activePriceObj.label;
  }

  let activeMetals: string[] = [];
  if (router.query.metal) {
    activeMetals =
      typeof router.query.metal === "string"
        ? [router.query.metal]
        : (router.query.metal as string[]);
  }

  let activeOccasions: string[] = [];
  if (router.query.occasion) {
    activeOccasions =
      typeof router.query.occasion === "string"
        ? [router.query.occasion]
        : (router.query.occasion as string[]);
  }

  const clearCategory = () => {
    router.push({ pathname: "/jewellery" });
  };

  const clearPrice = () => {
    const { priceMin, priceMax, ...rest } = router.query;
    router.replace(
      {
        pathname: isSearch ? "/jewellery" : "/jewellery/category/[category]",
        query: {
          ...combineCategoryFilters(rest),
          ...(isSearch
            ? { q: router.query.q }
            : { category: categoryState.data?.slug }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const clearMetal = (metalVal: string) => {
    const newMetals = activeMetals.filter((m) => m !== metalVal);
    router.replace(
      {
        pathname: isSearch ? "/jewellery" : "/jewellery/category/[category]",
        query: {
          ...combineCategoryFilters({ ...router.query, metal: newMetals }),
          ...(isSearch
            ? { q: router.query.q }
            : { category: categoryState.data?.slug }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleOccasionChange = (occasionVal: string) => {
    let newOccasions = [...activeOccasions];
    if (newOccasions.includes(occasionVal)) {
      newOccasions = newOccasions.filter((o) => o !== occasionVal);
    } else {
      newOccasions.push(occasionVal);
    }

    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({ ...router.query, occasion: newOccasions }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({ ...router.query, occasion: newOccasions }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const clearOccasion = (occasionVal: string) => {
    handleOccasionChange(occasionVal);
  };

  const clearAll = () => {
    router.push({ pathname: "/jewellery" });
  };

  const hasAnyFilter =
    activeCategory || activePriceLabel || activeMetals.length > 0 || activeOccasions.length > 0;

  const [openAccordions, setOpenAccordions] = useState([
    "categories",
    "price",
    "metal",
    "occasion",
  ]);

  const toggleAccordion = (name: string) => {
    if (openAccordions.includes(name)) {
      setOpenAccordions(openAccordions.filter((a) => a !== name));
    } else {
      setOpenAccordions([...openAccordions, name]);
    }
  };

  // Data processing for mobile drawer
  const categoryFacet = categoryState?.facets?.multilevel?.list?.find(
    (x: any) => x.filterField == "categoryPath"
  );
  const categoryDisplayName = categoryFacet?.displayName || "Categories";
  const categoryValues = categoryFacet?.values || [];
  const categoryItems = categoryValues.map((v: any) => ({
    label: `${v.name} (${v.count})`,
    value: v.name,
  }));

  const metalArray = categoryState?.facets?.text?.list?.find(
    (x: any) => x.facetName == "vPurity_uFilter"
  )?.values;
  const metalItems =
    metalArray?.reduce((acc: any, cur: any, index: any) => {
      if (index % 2 === 0) {
        acc.push({ label: `${cur} (${metalArray[index + 1]})`, value: cur });
      }
      return acc;
    }, []) || [];

  const occasionArray = categoryState?.facets?.text?.list?.find(
    (x: any) => x.facetName == "vOccasions_uFilter"
  )?.values;
  const occasionItems =
    occasionArray?.reduce((acc: any, cur: any, index: any) => {
      if (index % 2 === 0) {
        acc.push({ label: `${cur} (${occasionArray[index + 1]})`, value: cur });
      }
      return acc;
    }, []) || [];

  const handleCategoryChange = async (catName: string) => {
    if (!catName || catName === "All Products") {
      router.push({ pathname: "/jewellery" });
      return;
    }
    try {
      const slug = await resolveCategorySlug(
        catName,
        isSearch ? undefined : categoryState.data?.id
      );
      if (slug) {
        router.push({ pathname: `/jewellery/category/${slug}` });
      }
    } catch (error) {
      router.push({ pathname: "/jewellery", query: { category: catName } });
    }
  };

  const activeCategoryCount = activeCategory ? 1 : 0;
  const activePriceCount = activePriceLabel ? 1 : 0;
  const activeMetalCount = activeMetals.length;

  const handlePriceChange = (value: { min?: number; max?: number }) => {
    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({
              ...router.query,
              priceMin: value.min,
              priceMax: value.max,
            }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({
              ...router.query,
              priceMin: value.min,
              priceMax: value.max,
            }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const handleMetalChange = (metalVal: string) => {
    let newMetals = [...activeMetals];
    if (newMetals.includes(metalVal)) {
      newMetals = newMetals.filter((m) => m !== metalVal);
    } else {
      newMetals.push(metalVal);
    }

    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({ ...router.query, metal: newMetals }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({ ...router.query, metal: newMetals }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const sortFilterChange = (value: any) => {
    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({
              ...router.query,
              sort: value,
            }),
            q: router.query.q,
            try_it_online: router.query.try_it_online,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({
              ...router.query,
              sort: value,
            }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
    setShowSort(false);
  };

  const sortFilters = [
    { label: "Relevance", value: "" },
    { label: "Price: Low to High", value: "price-asc" },
    { label: "Price: High to Low", value: "price-desc" },
  ];

  const fastDeliveryChange = (event: any) => {
    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({
              ...router.query,
              fastDelivery: event.target.checked,
            }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
      return;
    }
    router.replace(
      {
        pathname: "/jewellery/category/[category]",
        query: {
          category: categoryState.data?.slug,
          ...combineCategoryFilters({
            ...router.query,
            fastDelivery: event.target.checked,
          }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <div className={`${styles.container} ${"container"}`}>
      <div className="row mb-0 mt-1">
        <div className="col-12">
          {isSearch && router.query.q && (
            <p>Search results for &apos;{router.query.q}&apos;</p>
          )}
        </div>
      </div>
      <div className={styles.desktop_filter}>
        <div className="row">
          <div className="w-100 display-fl align-items-center">
            <div className="display-fl align-items-center">
              <DesktopFilterDropdown isSearch={isSearch} />
            </div>
            <div className="display-fl align-items-center">
              <div className={styles.checkbox}>
                <div className={styles.check}>
                  <label
                    className="fast_red pointer"
                    htmlFor="fast-delivery-filter"
                  >
                    <Image className={styles.fast} src={fast} alt="" /> Fast
                    Delivery
                  </label>
                  <label className={styles.switch}>
                    <input
                      id="fast-delivery-filter"
                      type="checkbox"
                      checked={router.query?.fastDelivery ? true : false}
                      onChange={fastDeliveryChange}
                    />
                    <span className={`${styles.slider} ${styles.round}`}></span>
                  </label>
                </div>
              </div>
              <div className={styles.filter_section1}>
                <div className={styles.filter_section2}>
                  <span>Sort By:</span>
                  <SortFilter isSearch={isSearch} />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-9">
            <p className="mb-1 mt-3">
              <PaginationCounter />
            </p>
          </div>
          <div className="col-md-3">
            {/* <div className={styles.filter_section2}>
              <span>Sort By:</span>
              <SortFilter isSearch={isSearch} />
            </div> */}
          </div>
        </div>
        <div className="cleafix"></div>
      </div>
      <div className={styles.mobile_filter}>
        <div className={styles.filter_controls}>
          <button
            className={styles.icon_btn}
            onClick={() => {
              setShowFilter(!showFilter);
              setShowSort(false);
            }}
            aria-label="Filter"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" />
            </svg>
          </button>
          <button
            className={styles.icon_btn}
            onClick={() => {
              setShowSort(!showSort);
              setShowFilter(false);
            }}
            aria-label="Sort"
          >
            <svg
              width="15"
              height="12"
              viewBox="0 0 15 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.37934 3.5686L3.49045 0.601562L0.601562 3.5686M3.48794 10.6016V0.601562M13.6016 7.63453L10.7127 10.6016L7.82378 7.63453M10.7102 0.601562V10.6016"
                stroke="#121212"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className={styles.active_chips}>
            {activeCategory && (
              <div className={styles.chip}>
                {activeCategory}
                <span onClick={clearCategory}>✕</span>
              </div>
            )}
            {activePriceLabel && (
              <div className={styles.chip}>
                {activePriceLabel}
                <span onClick={clearPrice}>✕</span>
              </div>
            )}
            {activeMetals.map((metal) => (
              <div key={metal} className={styles.chip}>
                {metal}
                <span onClick={() => clearMetal(metal)}>✕</span>
              </div>
            ))}
            {activeOccasions.map((occ) => (
              <div key={occ} className={styles.chip}>
                {occ}
                <span onClick={() => clearOccasion(occ)}>✕</span>
              </div>
            ))}
            {hasAnyFilter && (
              <button className={styles.clear_all} onClick={clearAll}>
                Clear All
              </button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={styles.mobile_filter_overlay}>
            <div className={styles.mobile_filter_drawer}>
              <div className={styles.drawer_header}>
                <h3>Filter By</h3>
                <button
                  onClick={() => setShowFilter(false)}
                  aria-label="Close"
                  className={styles.close_btn}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#333"
                    strokeWidth="2"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div className={styles.drawer_content}>
                <div className={styles.fast_delivery_mobile}>
                  <div className={styles.airplane_wrapper}>
                    <Image src={fast} alt="Fast Delivery" width={24} height={24} />
                    <span>Fast Delivery</span>
                  </div>
                  <label className={styles.mobile_switch}>
                    <input
                      type="checkbox"
                      checked={router.query?.fastDelivery ? true : false}
                      onChange={fastDeliveryChange}
                    />
                    <span className={`${styles.slider} ${styles.round}`}></span>
                  </label>
                </div>

                {/* Categories Accordion */}
                <div className={styles.mobile_accordion}>
                  <div
                    className={styles.accordion_title}
                    onClick={() => toggleAccordion("categories")}
                  >
                    <span>
                      {categoryDisplayName} ({activeCategoryCount})
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{
                        transform: openAccordions.includes("categories")
                          ? "rotate(180deg)"
                          : "none",
                        transition: "0.3s",
                      }}
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  {openAccordions.includes("categories") && (
                    <div className={styles.accordion_body}>
                      {categoryItems.map((cat: any) => (
                        <label
                          key={cat.value}
                          className={styles.mobile_checkbox_row}
                        >
                          <input
                            type="checkbox"
                            checked={activeCategory === cat.value}
                            onChange={() => handleCategoryChange(cat.value)}
                          />
                          <span>{cat.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Accordion */}
                <div className={styles.mobile_accordion}>
                  <div
                    className={styles.accordion_title}
                    onClick={() => toggleAccordion("price")}
                  >
                    <span>Price ({activePriceCount})</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{
                        transform: openAccordions.includes("price")
                          ? "rotate(180deg)"
                          : "none",
                        transition: "0.3s",
                      }}
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  {openAccordions.includes("price") && (
                    <div className={styles.accordion_body}>
                      {priceFilters.map((p) => {
                        const isChecked = activePriceLabel === p.label;
                        return (
                          <label
                            key={p.label}
                            className={styles.mobile_checkbox_row}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                if (isChecked) clearPrice();
                                else handlePriceChange(p.value);
                              }}
                            />
                            <span>{p.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Metal Accordion */}
                <div className={styles.mobile_accordion}>
                  <div
                    className={styles.accordion_title}
                    onClick={() => toggleAccordion("metal")}
                  >
                    <span>Metal ({activeMetalCount})</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{
                        transform: openAccordions.includes("metal")
                          ? "rotate(180deg)"
                          : "none",
                        transition: "0.3s",
                      }}
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  {openAccordions.includes("metal") && (
                    <div className={styles.accordion_body}>
                      {metalItems.map((m: any) => {
                        const isChecked = activeMetals.includes(m.value);
                        return (
                          <label
                            key={m.value}
                            className={styles.mobile_checkbox_row}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleMetalChange(m.value)}
                            />
                            <span>{m.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Occasion Accordion */}
                <div className={styles.mobile_accordion}>
                  <div
                    className={styles.accordion_title}
                    onClick={() => toggleAccordion("occasion")}
                  >
                    <span>Occasion ({activeOccasions.length})</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{
                        transform: openAccordions.includes("occasion")
                          ? "rotate(180deg)"
                          : "none",
                        transition: "0.3s",
                      }}
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  {openAccordions.includes("occasion") && (
                    <div className={styles.accordion_body}>
                      {occasionItems.map((m: any) => {
                        const isChecked = activeOccasions.includes(m.value);
                        return (
                          <label
                            key={m.value}
                            className={styles.mobile_checkbox_row}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleOccasionChange(m.value)}
                            />
                            <span>{m.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className={styles.drawer_footer}>
                  <button className={styles.clear_all_btn} onClick={clearAll}>
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSort && (
          <div className={styles.mobile_filter_overlay}>
            <div
              className={`${styles.mobile_filter_drawer} ${styles.sort_drawer}`}
            >
              <div className={styles.drawer_header}>
                <h3>Sort By</h3>
                <button
                  onClick={() => setShowSort(false)}
                  aria-label="Close"
                  className={styles.close_btn}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#333"
                    strokeWidth="2"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div className={styles.drawer_content}>
                <div className={styles.sort_list}>
                  {sortFilters.map((sort) => {
                    const isSelected = (router.query.sort || "") === sort.value;
                    return (
                      <button
                        key={sort.value}
                        className={`${styles.sort_option} ${
                          isSelected ? styles.active_sort : ""
                        }`}
                        onClick={() => sortFilterChange(sort.value)}
                      >
                        {sort.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="col-md-12">
          {categoryState.products?.data && (
            <p className="mb-1 mt-3">
              Showing 1 -{" "}
              {categoryState.products.size * (categoryState.products.page - 1) +
                categoryState.products.data.length}{" "}
              of {categoryState.products.totalRecords}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatgeoryFiltersUnbxd;
